import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRotateLeft, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  getCompanyPersonaSchema,
  getPersonaSchemaTemplates,
  saveCompanyPersonaSchema,
} from '../../services/api';
import './CompanyPersonaSchemaPanel.scss';

const FALLBACK_PRESETS = [
  { id: 'telecom', label: '통신 (Telecom)' },
  { id: 'finance', label: '금융/투자 (Finance)' },
];

const emptyRow = () => ({ key: '', label: '', hints: '' });

function normalizeRows(variables) {
  if (!Array.isArray(variables)) return [emptyRow()];
  const rows = variables.map((item) => ({
    key: item.key || '',
    label: item.label || '',
    hints: item.hints || '',
  }));
  return rows.length ? rows : [emptyRow()];
}

function CompanyPersonaSchemaPanel({ companyId, disabled = false }) {
  const [presets, setPresets] = useState(FALLBACK_PRESETS);
  const [templateId, setTemplateId] = useState('telecom');
  const [label, setLabel] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [source, setSource] = useState('registry');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [baseline, setBaseline] = useState(null);

  const applyPayload = useCallback((data) => {
    const nextTemplate = data.template_id || data.templateId || data.preset_id || data.presetId || 'telecom';
    const nextLabel = data.label || '';
    const nextRows = normalizeRows(data.variables);
    const nextSource = data.source || 'registry';
    setTemplateId(nextTemplate);
    setLabel(nextLabel);
    setRows(nextRows);
    setSource(nextSource);
    setBaseline({
      templateId: nextTemplate,
      label: nextLabel,
      rows: nextRows,
      source: nextSource,
    });
  }, []);

  const fetchSchema = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      setNotice(null);
      const data = await getCompanyPersonaSchema(companyId);
      applyPayload(data);
    } catch (err) {
      console.error(err);
      setError(err.message || '변수 스키마를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [companyId, applyPayload]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPersonaSchemaTemplates();
        const list = data.templates || [];
        if (!cancelled && list.length) {
          setPresets(
            list.map((t) => ({
              id: t.id,
              label: t.builtin ? (t.label || t.id) : `${t.label || t.id} (커스텀)`,
              basePresetId: t.base_preset_id || t.basePresetId || t.id,
              builtin: !!t.builtin,
            }))
          );
        }
      } catch (err) {
        console.error('template list failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  const dirty = useMemo(() => {
    if (!baseline) return false;
    return (
      baseline.templateId !== templateId
      || baseline.label !== label
      || JSON.stringify(baseline.rows) !== JSON.stringify(rows)
    );
  }, [baseline, templateId, label, rows]);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (index) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyRow()];
    });
  };

  const loadTemplateVariables = async () => {
    try {
      setError(null);
      setNotice(null);
      const data = await getCompanyPersonaSchema(companyId, { presetId: templateId });
      setRows(normalizeRows(data.variables));
      if (!label.trim()) {
        setLabel(data.label || '');
      }
      setNotice('템플릿 변수 목록을 불러왔습니다. 저장해야 반영됩니다.');
    } catch (err) {
      console.error(err);
      setError(err.message || '템플릿 변수를 불러오지 못했습니다.');
    }
  };

  const handleSave = async () => {
    const variables = rows
      .map((row) => ({
        key: row.key.trim(),
        label: row.label.trim(),
        hints: row.hints.trim(),
      }))
      .filter((row) => row.key);

    if (!variables.length) {
      setError('변수를 최소 1개 입력하거나, 템플릿으로 되돌리기를 사용하세요.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      const data = await saveCompanyPersonaSchema(companyId, {
        templateId,
        label: label.trim() || undefined,
        variables,
        basePresetId: presets.find((p) => p.id === templateId)?.basePresetId,
      });
      applyPayload(data);
      setNotice(data.message || '회사 변수 스키마를 저장했습니다.');
    } catch (err) {
      console.error(err);
      setError(err.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToTemplate = async () => {
    if (!window.confirm('회사 커스텀 변수 스키마를 지우고 템플릿 기본값으로 되돌릴까요?')) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      const data = await saveCompanyPersonaSchema(companyId, {
        templateId,
        variables: [],
      });
      applyPayload(data);
      setNotice(data.message || '템플릿 기본값으로 되돌렸습니다.');
    } catch (err) {
      console.error(err);
      setError(err.message || '되돌리기에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-persona-schema-panel sa-table-container">
      <div className="sa-table-toolbar schema-toolbar">
        <div className="sa-table-toolbar-left">
          <h3 style={{ margin: 0, color: '#111827', fontSize: 16 }}>Persona 변수 스키마</h3>
          <span className={`schema-source-pill source-${source}`}>
            {source === 'company' ? '회사 커스텀' : '템플릿 기본'}
          </span>
        </div>
        <div className="schema-toolbar-actions">
          <button
            type="button"
            className="sa-btn sa-btn-secondary sa-btn-sm"
            onClick={loadTemplateVariables}
            disabled={disabled || loading || saving}
          >
            템플릿 변수 불러오기
          </button>
          <button
            type="button"
            className="sa-btn sa-btn-secondary sa-btn-sm"
            onClick={handleResetToTemplate}
            disabled={disabled || loading || saving || source === 'registry'}
          >
            <FontAwesomeIcon icon={faRotateLeft} /> 템플릿으로 되돌리기
          </button>
          <button
            type="button"
            className="sa-btn sa-btn-primary sa-btn-sm"
            onClick={handleSave}
            disabled={disabled || loading || saving || !dirty}
          >
            <FontAwesomeIcon icon={faSave} /> {saving ? '저장 중…' : '스키마 저장'}
          </button>
        </div>
      </div>

      <p className="schema-help">
        이 회사의 인터뷰 청킹·evidence 검색에 쓰는 DNA 변수 목록입니다.
        같은 템플릿(금융/통신)이어도 회사마다 변수를 다르게 둘 수 있습니다.
      </p>

      {loading && <div className="sa-loading">스키마 불러오는 중…</div>}

      {(error || notice) && (
        <div className={`schema-banner ${error ? 'error' : 'ok'}`}>
          {error || notice}
        </div>
      )}

      {!loading && (
        <>
          <div className="schema-meta-row">
            <div className="field-group">
              <label htmlFor="schema-template">기반 템플릿</label>
              <select
                id="schema-template"
                className="sa-filter-select"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={disabled || saving}
              >
                {presets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label || preset.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="field-group grow">
              <label htmlFor="schema-label">스키마 표시명 (선택)</label>
              <input
                id="schema-label"
                className="sa-search-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="예: research2 커스텀 금융 DNA"
                disabled={disabled || saving}
              />
            </div>
          </div>

          <table className="sa-table schema-table">
            <thead>
              <tr>
                <th style={{ width: '22%' }}>변수 key</th>
                <th style={{ width: '24%' }}>라벨</th>
                <th>검색 힌트</th>
                <th style={{ width: 56 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`var-${index}`}>
                  <td>
                    <input
                      className="schema-input"
                      value={row.key}
                      onChange={(e) => updateRow(index, 'key', e.target.value)}
                      placeholder="camelCaseKey"
                      disabled={disabled || saving}
                    />
                  </td>
                  <td>
                    <input
                      className="schema-input"
                      value={row.label}
                      onChange={(e) => updateRow(index, 'label', e.target.value)}
                      placeholder="화면 표시명"
                      disabled={disabled || saving}
                    />
                  </td>
                  <td>
                    <input
                      className="schema-input"
                      value={row.hints}
                      onChange={(e) => updateRow(index, 'hints', e.target.value)}
                      placeholder="RAG 검색용 키워드"
                      disabled={disabled || saving}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="schema-icon-btn"
                      onClick={() => removeRow(index)}
                      disabled={disabled || saving || rows.length <= 1}
                      aria-label="변수 삭제"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="schema-footer">
            <button
              type="button"
              className="sa-btn sa-btn-secondary sa-btn-sm"
              onClick={addRow}
              disabled={disabled || saving}
            >
              <FontAwesomeIcon icon={faPlus} /> 변수 추가
            </button>
            <span className="schema-count">{rows.filter((r) => r.key.trim()).length}개 변수</span>
          </div>
        </>
      )}
    </div>
  );
}

export default CompanyPersonaSchemaPanel;
